import React, { useState, useEffect } from "react";
import { useAuth } from "contexts/AuthContext";
import { useToast } from "contexts/ToastContext";
import { useTranslation } from "react-i18next";
import {
  createLegislativeProsecutorAssignment,
  updateLegislativeProsecutorAssignment,
  deleteLegislativeProsecutorAssignment,
  getLegislativeDrafting,
} from "services/legislativeService";
import { getEmployeesByAdminAndOrg } from "services/getEmployees";
import { getInitials } from "utils/helpers";
import { X, Trash2, Award, Shield, UserPlus, Search, RefreshCw } from "lucide-react";

interface TeamMember {
  id: string;
  prosecutorUserId: string;
  name: string;
  role: string;
  isTeamLeader: boolean;
}

interface EmployeeData {
  user: {
    id: number | string;
    first_name: string;
    last_name?: string;
  };
  roles: string[];
  department?: string;
}

interface EditTeamModalProps {
  request: any;
  isOpen: boolean;
  onClose: () => void;
  setFormState: Function;
}

const EditTeamModal: React.FC<EditTeamModalProps> = ({
  request,
  isOpen,
  onClose,
  setFormState,
}) => {
  const { t } = useTranslation();
  const contextData = useAuth();
  const { showToast } = useToast();
  const [teamMembers, setTeamMembers] = useState<TeamMember[]>([]);
  const [submitting, setSubmitting] = useState(false);
  const [showAddMember, setShowAddMember] = useState(false);
  const [availableEmployees, setAvailableEmployees] = useState<EmployeeData[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedEmployee, setSelectedEmployee] = useState<EmployeeData | null>(null);
  const [loadingEmployees, setLoadingEmployees] = useState(false);

  // Load current team members from the request's prosecutor assignments
  useEffect(() => {
    if (isOpen) {
      setTeamMembers(
        (request.prosecutor_assignments ?? []).map((member: any) => ({
          id: String(member.id),
          prosecutorUserId: String(member.prosecutor_user_id ?? member.id),
          name: member.prosecutor_name || member.name,
          role: "Prosecutor",
          isTeamLeader: member.is_team_leader ?? member.role === "leader" ?? false,
        }))
      );
    }
  }, [isOpen, request]);

  // Fetch available prosecutors when adding a member
  useEffect(() => {
    if (showAddMember && isOpen) {
      setLoadingEmployees(true);
      getEmployeesByAdminAndOrg({
        bearer: contextData.token || contextData?.user?.access_token || "",
        admin_unit: contextData?.user?.admin_unit || "",
        org_unit: contextData?.user?.org_unit || "",
      })
        .then((data: EmployeeData[]) => {
          const prosecutors = data.filter((emp) =>
            emp.roles?.some((role: string) => role === "LegalResearchProsecutor")
          );
          const currentIds = teamMembers.map((m) => m.prosecutorUserId);
          setAvailableEmployees(
            prosecutors.filter((emp) => !currentIds.includes(String(emp.user.id)))
          );
        })
        .catch((error) => {
          console.error("Error fetching employees:", error);
          showToast(t("FailedToFetchEmployees") || "Failed to fetch employees.", "error");
        })
        .finally(() => setLoadingEmployees(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showAddMember, isOpen]);

  const refreshData = async () => {
    try {
      const response = await getLegislativeDrafting(request.id);
      setFormState((prev: any) => ({ ...prev, ...response }));
    } catch (error) {
      console.error("Error refreshing data:", error);
    }
  };

  const handleSetTeamLeader = async (member: TeamMember) => {
    setSubmitting(true);
    try {
      const currentLeader = teamMembers.find(
        (m) => m.isTeamLeader && m.id !== member.id
      );
      if (currentLeader) {
        const removeData = new FormData();
        removeData.append("is_team_leader", "false");
        removeData.append("role", "member");
        await updateLegislativeProsecutorAssignment(currentLeader.id, removeData);
      }

      const formData = new FormData();
      formData.append("is_team_leader", "true");
      formData.append("role", "leader");
      await updateLegislativeProsecutorAssignment(member.id, formData);

      setTeamMembers((prev) =>
        prev.map((m) => ({ ...m, isTeamLeader: m.id === member.id }))
      );
      showToast(t("TeamLeaderUpdatedSuccess") || "Team leader updated.", "success");
      await refreshData();
    } catch (error) {
      console.error("Error updating team leader:", error);
      showToast(t("FailedToUpdateTeamLeader") || "Failed to update team leader.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleRemoveMember = async (member: TeamMember) => {
    if (!window.confirm(t("ConfirmRemoveTeamMember") || "Remove this team member?")) {
      return;
    }
    setSubmitting(true);
    try {
      await deleteLegislativeProsecutorAssignment(member.id);
      setTeamMembers((prev) => prev.filter((m) => m.id !== member.id));
      showToast(t("TeamMemberRemovedSuccess") || "Team member removed.", "success");
      await refreshData();
    } catch (error) {
      console.error("Error removing team member:", error);
      showToast(t("FailedToRemoveTeamMember") || "Failed to remove team member.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleAddMember = async () => {
    if (!selectedEmployee) {
      showToast(t("PleaseSelectTeamMember") || "Please select a team member.", "error");
      return;
    }
    setSubmitting(true);
    try {
      const fullName = `${selectedEmployee.user.first_name} ${
        selectedEmployee.user.last_name || ""
      }`.trim();
      const formData = new FormData();
      formData.append("instruction", String(request.id));
      formData.append("prosecutor_user_id", String(selectedEmployee.user.id));
      formData.append("prosecutor_name", fullName);
      formData.append("role", teamMembers.length === 0 ? "leader" : "member");
      formData.append("is_team_leader", teamMembers.length === 0 ? "true" : "false");

      await createLegislativeProsecutorAssignment(formData);

      setShowAddMember(false);
      setSelectedEmployee(null);
      setSearchQuery("");
      showToast(t("TeamMemberAddedSuccess") || "Team member added.", "success");
      await refreshData();
    } catch (error) {
      console.error("Error adding team member:", error);
      showToast(t("FailedToAddTeamMember") || "Failed to add team member.", "error");
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  const filteredEmployees = availableEmployees.filter((emp) => {
    const fullName = `${emp.user.first_name} ${emp.user.last_name || ""}`
      .trim()
      .toLowerCase();
    return fullName.includes(searchQuery.toLowerCase());
  });

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md mx-4 max-h-[90vh] overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h2 className="text-lg font-semibold text-gray-900">{t("EditTeam") || "Edit Team"}</h2>
          <button
            onClick={onClose}
            className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Content */}
        <div className="px-6 py-4 max-h-[60vh] overflow-y-auto">
          {showAddMember ? (
            <div className="mb-4 p-4 bg-gray-50 rounded-lg">
              <h3 className="font-medium text-gray-900 mb-3">
                {t("AddNewTeamMember") || "Add New Team Member"}
              </h3>
              {loadingEmployees ? (
                <div className="text-center py-4 text-gray-500">{t("Loading") || "Loading..."}</div>
              ) : (
                <>
                  <div className="relative mb-3">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <input
                      type="text"
                      placeholder={t("SearchProsecutors") || "Search prosecutors..."}
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="w-full pl-10 pr-4 py-2 border rounded-lg focus:ring-2 focus:ring-gray-800 focus:border-transparent"
                    />
                  </div>
                  <div className="max-h-40 overflow-y-auto mb-3 border rounded-lg">
                    {filteredEmployees.length === 0 ? (
                      <div className="p-3 text-center text-gray-500 text-sm">
                        {t("NoAvailableProsecutorsFound") || "No available prosecutors found."}
                      </div>
                    ) : (
                      filteredEmployees.map((emp) => (
                        <div
                          key={emp.user.id}
                          onClick={() => setSelectedEmployee(emp)}
                          className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-gray-100 ${
                            String(selectedEmployee?.user.id) === String(emp.user.id)
                              ? "bg-gray-100"
                              : ""
                          }`}
                        >
                          <div className="w-8 h-8 rounded-full bg-gray-300 flex items-center justify-center text-xs font-medium">
                            {getInitials(`${emp.user.first_name} ${emp.user.last_name || ""}`)}
                          </div>
                          <div>
                            <p className="font-medium text-sm">
                              {emp.user.first_name} {emp.user.last_name}
                            </p>
                            <p className="text-xs text-gray-500">
                              {emp.department || t("LegalDepartment") || "Legal Department"}
                            </p>
                          </div>
                        </div>
                      ))
                    )}
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => {
                        setShowAddMember(false);
                        setSelectedEmployee(null);
                        setSearchQuery("");
                      }}
                      className="flex-1 py-2 px-4 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50"
                    >
                      {t("Cancel") || "Cancel"}
                    </button>
                    <button
                      onClick={handleAddMember}
                      disabled={!selectedEmployee || submitting}
                      className="flex-1 py-2 px-4 bg-[#212121] text-white rounded-lg hover:bg-gray-800 disabled:opacity-50"
                    >
                      {submitting ? t("Adding") || "Adding..." : t("AddMember") || "Add Member"}
                    </button>
                  </div>
                </>
              )}
            </div>
          ) : (
            <div className="flex justify-end mb-3">
              <button
                onClick={() => setShowAddMember(true)}
                className="flex items-center gap-2 px-3 py-2 text-sm bg-[#212121] text-white rounded-lg hover:bg-gray-800"
              >
                <UserPlus className="w-4 h-4" />
                {t("AddMember") || "Add Member"}
              </button>
            </div>
          )}

          {teamMembers.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              {t("NoTeamMembersFound") || "No team members found."}
            </div>
          ) : (
            <div className="space-y-3">
              {teamMembers.map((member) => (
                <div
                  key={member.id}
                  className={`flex items-center justify-between p-3 rounded-lg border ${
                    member.isTeamLeader ? "border-[#AA232F] bg-[#AA232F]/5" : "border-gray-200"
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full bg-gray-300 flex items-center justify-center text-sm font-medium">
                      {getInitials(member.name)}
                    </div>
                    <p className="font-medium text-gray-900">{member.name}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {member.isTeamLeader && (
                      <span className="flex items-center gap-1 px-2 py-1 bg-[#AA232F]/10 text-[#AA232F] text-xs rounded-full">
                        <Award className="w-3 h-3" />
                        {t("TeamLeader") || "Team Leader"}
                      </span>
                    )}
                    {!member.isTeamLeader && (
                      <button
                        onClick={() => handleSetTeamLeader(member)}
                        disabled={submitting}
                        className="p-2 text-gray-500 hover:text-[#AA232F] hover:bg-[#AA232F]/5 rounded-lg transition-colors"
                        title={t("SetAsTeamLeader") || "Set as team leader"}
                      >
                        <Shield className="w-4 h-4" />
                      </button>
                    )}
                    <button
                      onClick={() => handleRemoveMember(member)}
                      disabled={submitting}
                      className="p-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      title={t("RemoveMember") || "Remove member"}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="px-6 py-4 border-t bg-gray-50">
          <button
            onClick={async () => {
              await refreshData();
              onClose();
            }}
            className="w-full py-2 px-4 bg-[#212121] text-white rounded-lg hover:bg-gray-800 transition-colors flex items-center justify-center gap-2"
          >
            <RefreshCw className="w-4 h-4" />
            {t("CloseAndRefresh") || "Close & Refresh"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default EditTeamModal;
